import * as React from 'react';
import { debounce } from 'lodash';
import { JobExecution } from '@lct-jobs/core';
import { getMassOperationName } from 'components/JobExecution/massOperationType';

/**
 * A search keeps paging until it has gathered a useful number of matches, the way the Learn Sites
 * search does. Without it a job that last ran months ago stays invisible until the user has
 * clicked Load More enough times to reach it, which is the problem the search box exists to solve.
 */
const MIN_MATCHES_BEFORE_PAGING_STOPS = 30;

/**
 * An upper bound on that walk. Executions lists run to thousands of pages, so a term that matches
 * nothing would otherwise fire a request per page from a single keystroke. On reaching the cap the
 * search stops and says it has only looked through part of the stream; Load More continues it by
 * hand.
 */
export const MAX_SEARCH_PAGES = 20;

/**
 * Pages fetched while walking are asked for larger than the default,Mirrors SEARCH_PAGE_SIZE in Learn Sites.
 */
export const SEARCH_PAGE_LIMIT = 200;

const DEBOUNCE_MS = 250;

/**
 * Both the name on screen and the underlying job name are searchable. A mass execution renders as
 * its child job or query name, so matching only `jobName` would miss what the user can see, and
 * matching only the rendered name would miss someone typing "Mass Job Executor".
 */
const searchableText = (execution: JobExecution) =>
  `${getMassOperationName(execution) ?? ''} ${execution.jobName ?? ''}`.toLowerCase();

export interface ExecutionSearch {
  /** Raw field value, updated on every keystroke. */
  readonly inputValue: string;
  readonly onInputChange: (value: string) => void;
  /** True once a debounced, non-empty term is in effect. */
  readonly isSearching: boolean;
  /** The stream ran out with nothing matching, so there is nothing left to page for. */
  readonly searchExhausted: boolean;
  /** The walk hit its page cap, so the matches shown cover only part of the stream. */
  readonly searchCapped: boolean;
  /**
   * Nothing matches yet but the walk is still fetching pages, so "no match" would be premature —
   * the row the user is after may be on a page that has not arrived.
   */
  readonly searchPending: boolean;
  readonly matches: JobExecution[];
  readonly searchTerm: string;
  /**
   * What the Load More button should call. A click during a search continues the same walk by
   * hand, so it asks for the same larger page the walk used — otherwise continuing past the page
   * cap crawls at the default size. Plain browsing keeps the default.
   */
  readonly loadMorePage: () => void;
}

export interface UseExecutionSearchOptions {
  executions: JobExecution[];
  nextPageToken?: string;
  loading: boolean;
  loadMore: (options?: { limit?: number }) => void;
}

export const useExecutionSearch = ({
  executions,
  nextPageToken,
  loading,
  loadMore,
}: UseExecutionSearchOptions): ExecutionSearch => {
  const [inputValue, setInputValue] = React.useState('');
  const [searchTerm, setSearchTerm] = React.useState('');
  const [searchExhausted, setSearchExhausted] = React.useState(false);
  const [searchCapped, setSearchCapped] = React.useState(false);

  const applyTerm = React.useMemo(
    () =>
      debounce((value: string) => {
        setSearchTerm(value.trim());
        setSearchExhausted(false);
        setSearchCapped(false);
      }, DEBOUNCE_MS),
    [],
  );

  React.useEffect(() => () => applyTerm.cancel(), [applyTerm]);

  const onInputChange = React.useCallback(
    (value: string) => {
      setInputValue(value);
      if (value.trim() === '') {
        // Clearing is immediate: the full list is already loaded, so there is nothing to wait for.
        applyTerm.cancel();
        setSearchTerm('');
        setSearchExhausted(false);
        setSearchCapped(false);
        return;
      }
      applyTerm(value);
    },
    [applyTerm],
  );

  const isSearching = searchTerm !== '';

  const matches = React.useMemo(() => {
    if (!isSearching) {
      return executions;
    }
    const needle = searchTerm.toLowerCase();
    return executions.filter((execution) => searchableText(execution).includes(needle));
  }, [executions, isSearching, searchTerm]);

  // Walk the stream while a search is short of matches. The token is recorded so a response that
  // adds no matches cannot ask for the same page again, and the pages walked are counted so a term
  // that matches nothing cannot run the whole stream.
  const requestedToken = React.useRef<string | undefined>(undefined);
  const pagesWalked = React.useRef(0);
  const walkedTerm = React.useRef('');
  React.useEffect(() => {
    if (!isSearching) {
      requestedToken.current = undefined;
      pagesWalked.current = 0;
      walkedTerm.current = '';
      setSearchCapped(false);
      return;
    }

    // Each term gets its own budget. Editing the box without clearing it first would otherwise
    // inherit the previous term's walk, so a search after one that hit the cap would page nowhere.
    if (walkedTerm.current !== searchTerm) {
      walkedTerm.current = searchTerm;
      requestedToken.current = undefined;
      pagesWalked.current = 0;
    }

    if (loading) return;

    if (!nextPageToken) {
      if (matches.length === 0) {
        setSearchExhausted(true);
      }
      return;
    }

    if (matches.length >= MIN_MATCHES_BEFORE_PAGING_STOPS) return;

    if (pagesWalked.current >= MAX_SEARCH_PAGES) {
      setSearchCapped(true);
      return;
    }

    if (requestedToken.current === nextPageToken) return;

    requestedToken.current = nextPageToken;
    pagesWalked.current += 1;
    loadMore({ limit: SEARCH_PAGE_LIMIT });
  }, [isSearching, searchTerm, loading, nextPageToken, matches.length, loadMore]);

  const loadMorePage = React.useCallback(
    () => loadMore(isSearching ? { limit: SEARCH_PAGE_LIMIT } : undefined),
    [loadMore, isSearching],
  );

  return {
    inputValue,
    onInputChange,
    isSearching,
    searchExhausted,
    searchCapped,
    searchPending: isSearching && matches.length === 0 && !searchExhausted && !searchCapped,
    matches,
    searchTerm,
    loadMorePage,
  };
};

export default useExecutionSearch;
